/**
 * What Homecrowd invoices directly, and for how much.
 *
 * Olive bills its own network; we only bill merchants we signed and added to
 * Olive. Nothing in the Olive payload marks the difference, so the
 * HomecrowdMerchant list (superuser-maintained) is the record of it, and this
 * module is the single place that decides whether a transaction is billable.
 * The Billing list page and the invoice page both import from here so the
 * money on the invoice always matches the money on the summary row.
 */

// The fallback when neither the transaction nor the merchant record carries a
// rate. Same 5% the Invoices page has always assumed.
import { isNonBillableTxn } from './transactionRevenue';
import { FAILED, REFUNDED, everWorthSomething, funnelStatusFor } from './funnel';

export const DEFAULT_RATE = 0.05;

/**
 * Stripe's fee for pulling the invoice by ACH, passed on to the merchant.
 *
 * One definition, used by the invoice sheet and by the debit itself, so the
 * number a merchant reads is the number that leaves their account.
 *
 * Stripe caps this fee at $5.00 per ACH debit. That cap is deliberately not
 * applied here — it is not what was asked for, and no invoice is near it (the
 * largest month is under $30, where 0.8% is 24 cents). It will start to matter
 * above a $625 invoice, at which point charging the full 0.8% recovers more
 * than Stripe actually took.
 */
export const STRIPE_ACH_FEE_RATE = 0.008;

/** The fee on a subtotal, rounded to whole cents the way the debit will be. */
export const stripeFeeFor = (subtotal) =>
  Math.round(Number(subtotal || 0) * STRIPE_ACH_FEE_RATE * 100) / 100;

/** What the merchant actually pays: commission plus the fee to collect it. */
export const invoiceTotalFor = (subtotal) =>
  Number(subtotal || 0) + stripeFeeFor(subtotal);

export const txnDate = (t) => t.auth_at || t.captured_at || t.created_at;

export const monthKeyFromTxn = (t) => {
  const iso = txnDate(t);
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export const monthLabel = (key) => {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

export const isOliveTxn = (t) =>
  Boolean(
    t.olive_merchant_id ||
    t.olive_merchant_name ||
    (t.raw_descriptor && String(t.raw_descriptor).toLowerCase().includes('olive'))
  );

export const merchantNameFromTxn = (t) =>
  t.olive_merchant_name || t.merchant_name || 'Unknown Merchant';

const normalizeName = (s) => String(s || '').trim().toLowerCase();

/**
 * Index the billable merchants for lookup.
 *
 * By offer id, which is the exact key: olive_transaction_webhook stores the
 * Olive offerId on Transaction.olive_merchant_id. Also by every store name, for
 * the fallback below.
 */
export const buildMerchantIndex = (merchants) => {
  const byOffer = new Map();
  const byName = new Map();
  for (const m of merchants || []) {
    if (!m) continue;
    for (const offerId of m.offer_ids || []) {
      if (offerId) byOffer.set(String(offerId), m);
    }
    const names = m.names && m.names.length ? m.names : [m.name];
    for (const name of names) {
      if (name) byName.set(normalizeName(name), m);
    }
  }
  return { byOffer, byName };
};

/**
 * The merchant a transaction belongs to, or null if we do not invoice it.
 *
 * Offer id first — that is the exact join. The name fallback exists because an
 * offer can expire out of the sync, and sync_olive_offer_locations skips
 * online-only stores entirely, so a merchant we really did add can be missing
 * from the snapshot even though its transactions are ours to bill.
 */
export const matchBillableMerchant = (t, index) => {
  if (!index || !isOliveTxn(t)) return null;

  const offerId = t.olive_merchant_id ? String(t.olive_merchant_id) : '';
  const byOffer = offerId ? index.byOffer.get(offerId) : null;
  if (byOffer) return byOffer;

  return index.byName.get(normalizeName(merchantNameFromTxn(t))) || null;
};

/**
 * What the merchant owes on one transaction. The commission Olive actually
 * reported wins; the rate Olive lists for that merchant is next; 5% is last.
 */
/**
 * The rate this merchant is actually on, as a decimal.
 *
 * Olive quotes cashback as a percentage: 4.0 means 4%. Invoices show this
 * rather than dividing each line's commission by its sale — that division
 * returns things like 6.984387838948233% because Olive's reported commission is
 * rounded to the cent, and a merchant should see the rate they agreed to.
 */
export const merchantRate = (merchant) => {
  const cashback = Number(merchant && merchant.cashback);
  return Number.isFinite(cashback) && cashback > 0 ? cashback / 100 : DEFAULT_RATE;
};

export const commissionFor = (t, merchant) => {
  const amount = Number(t.amount || 0);
  const reported = Number(t.commission_amount || 0);
  if (reported > 0) return reported;
  return amount * merchantRate(merchant);
};

/** Billable transactions grouped by month, then by merchant. Newest month first. */
export const groupBillableByMonth = (transactions, merchants) => {
  const index = buildMerchantIndex(merchants);
  const byMonth = new Map();

  for (const t of transactions || []) {
    // Failed, refunded and voided transactions have no sale standing behind
    // them, so there is nothing to take commission on. Vendors still report a
    // reward line against them, which is how merchants ended up invoiced for
    // purchases that never completed or were reversed.
    if (isNonBillableTxn(t)) continue;
    // The same judgement from the vendor's own event history: a commission
    // revised to zero is a return, and a negative one is a clawback. Neither is
    // visible in `status`, which stays 'pending' throughout.
    const state = funnelStatusFor(t, null);
    if (state === REFUNDED || state === FAILED) continue;
    // Never worth anything to anyone, so there is nothing to bill.
    if (!everWorthSomething(t)) continue;
    const merchant = matchBillableMerchant(t, index);
    if (!merchant) continue;
    const mKey = monthKeyFromTxn(t);
    if (!mKey) continue;

    if (!byMonth.has(mKey)) byMonth.set(mKey, new Map());
    const merchants_ = byMonth.get(mKey);
    // Key on the merchant, not the transaction's name, so two stores of the
    // same merchant land on one invoice.
    const rowKey = merchant.id;
    if (!merchants_.has(rowKey)) {
      merchants_.set(rowKey, {
        merchantId: merchant.id,
        name: merchant.name,
        brandId: merchant.brand_id || '',
        txnCount: 0,
        totalSales: 0,
        totalCommission: 0,
      });
    }
    const row = merchants_.get(rowKey);
    row.txnCount += 1;
    row.totalSales += Number(t.amount || 0);
    row.totalCommission += commissionFor(t, merchant);
  }

  return Array.from(byMonth.entries())
    .map(([key, merchants_]) => ({
      key,
      label: monthLabel(key),
      merchants: Array.from(merchants_.values()).sort(
        (a, b) => b.totalCommission - a.totalCommission
      ),
    }))
    .sort((a, b) => (a.key < b.key ? 1 : -1));
};

/**
 * Collection state for one merchant-month: colour, icon and label.
 *
 * One definition so the Billing list and the invoice page never describe the
 * same state differently. Invoice state wins over merchant state — once money
 * has been attempted, what happened to it is the more important fact.
 *
 * Colour carries health (green fine, amber waiting, red needs attention) and
 * the icon carries the specific state, so the three green states stay
 * distinguishable without relying on colour alone.
 */
export const PAYMENT_STATE = {
  paid: { color: '#1e7e34', icon: 'check', label: 'Paid' },
  processing: {
    color: '#1e7e34',
    icon: 'processing',
    label: 'Processing — ACH can take up to 4 business days',
  },
  ready: { color: '#1e7e34', icon: 'bank', label: 'Authorized — ready to debit' },
  pending: { color: '#c99700', icon: 'hourglass', label: 'Awaiting bank verification' },
  failed: { color: '#b00020', icon: 'alert', label: 'Failed — the debit did not go through' },
  disputed: {
    color: '#b00020',
    icon: 'alert',
    label: 'Disputed — returned by the bank, authorization revoked',
  },
  revoked: {
    color: '#b00020',
    icon: 'alert',
    label: 'Authorization revoked — the merchant must authorize again',
  },
  blocked: { color: '#b00020', icon: 'alert', label: 'Bank account blocked by Stripe' },
  canceled: { color: '#b00020', icon: 'alert', label: 'Canceled' },
  // Nothing is shown for a merchant that was never set up: an icon would imply
  // something is wrong, when in fact nothing has been started.
  none: { color: null, icon: null, label: 'Not set up for ACH billing' },
};

export const paymentStateFor = (profile, invoice) => {
  if (invoice && PAYMENT_STATE[invoice.status]) return PAYMENT_STATE[invoice.status];
  if (!profile) return PAYMENT_STATE.none;
  if (profile.can_be_charged) return PAYMENT_STATE.ready;
  return PAYMENT_STATE[profile.mandate_status] || PAYMENT_STATE.none;
};

/** Index the bulk overview for (merchantKey, period) lookups. */
export const buildBillingStateIndex = (overview) => {
  const profiles = new Map();
  for (const p of overview?.profiles || []) profiles.set(p.merchant_key, p);
  const invoices = new Map();
  for (const i of overview?.invoices || []) invoices.set(`${i.merchant_key}::${i.period}`, i);
  return {
    lookup: (merchantKey, period) =>
      paymentStateFor(profiles.get(merchantKey), invoices.get(`${merchantKey}::${period}`)),
  };
};
